import type { Authorization } from '@skop-app/lib-domain/types'

import type { UpsertProductVariables } from '@/services/mutations/product'

import {
  type OrganizationScope,
  resolveInheritedOrganizationIds,
  resolveOrganizationScope,
} from '@/utils/organization'

import { canMutateDomain, isGuestAuthorization } from './helpers'

export type OrderMutationOperation = 'create' | 'update' | 'delete'
export type OrderCustomerMutationType =
  | 'create'
  | 'update'
  | 'deleteDraft'
  | 'delete'

const ORDER_PERMISSION_BY_OPERATION = {
  create: 'canCreate',
  update: 'canUpdate',
  delete: 'canDelete',
} as const

export const canManageOrderCustomers = (
  authorization: Authorization | undefined,
  operation: OrderMutationOperation,
) =>
  canMutateDomain(
    authorization,
    'orders',
    ORDER_PERMISSION_BY_OPERATION[operation],
  )

export const canManageOrderServices = (
  authorization: Authorization | undefined,
  operation: OrderMutationOperation,
) =>
  canMutateDomain(
    authorization,
    'orders',
    ORDER_PERMISSION_BY_OPERATION[operation],
  )

export const canCreateOrderQuotes = (
  authorization: Authorization | undefined,
) => canMutateDomain(authorization, 'orders', 'canCreate')

export const resolveCustomerOrderMutationOperation = (
  upsertType: OrderCustomerMutationType,
): OrderMutationOperation => {
  switch (upsertType) {
    case 'create':
      return 'create'
    case 'update':
      return 'update'
    case 'delete':
    case 'deleteDraft':
      return 'delete'
    default:
      return 'update'
  }
}

export const resolveServiceOrderMutationOperation = (
  variables: Pick<UpsertProductVariables, 'upsertType' | 'input'>,
): OrderMutationOperation => {
  if (variables.upsertType === 'create') {
    return 'create'
  }

  if (
    variables.upsertType === 'update' &&
    variables.input.action === 'deleted'
  ) {
    return 'delete'
  }

  return 'update'
}

type OrderPartyIdentity = {
  orderUserId?: string | null
  organizationID?: string | null
  organizationMasterID?: string | null
  organization?: {
    id?: string | null
    parentID?: string | null
    masterID?: string | null
  } | null
  userID?: string | null
  user?: { id?: string | null } | null
  toOrganizationID?: string | null
  toUser?: { id?: string | null } | null
  shop?: { organizationID?: string | null; ownerID?: string | null } | null
}

// A known shop organization cannot be attributed through its owner's user ID.
export const isOrderRequesterShop = (order?: OrderPartyIdentity | null) => {
  const shopOrganizationID = order?.shop?.organizationID
  if (shopOrganizationID) {
    const requesterOrganization =
      order?.organizationID && order.organization?.id === order.organizationID
        ? order.organization
        : undefined
    const requesterScope = resolveOrganizationScope({
      ownership: order,
      selectedOrganization: requesterOrganization,
      selectedOrganizationParent: requesterOrganization?.parentID
        ? { id: requesterOrganization.parentID }
        : undefined,
    })
    if (shopOrganizationID === requesterScope.activeOrganizationId) return true
    if (
      order?.organizationMasterID &&
      requesterOrganization?.masterID &&
      order.organizationMasterID !== requesterOrganization.masterID
    )
      return false
    return Boolean(
      requesterScope.activeOrganizationId &&
      resolveInheritedOrganizationIds(requesterScope).includes(
        shopOrganizationID,
      ),
    )
  }
  const requesterUserID = order?.userID || order?.user?.id || order?.orderUserId
  return Boolean(
    order?.shop?.ownerID &&
    requesterUserID &&
    order.shop.ownerID === requesterUserID,
  )
}

// A buyer may read/manage a request without being its monetary issuer.
export const canConvertFavoriteToMonetaryOrder = ({
  authorization,
  organizationScope,
  userID,
  isLoadingSelectedOrganization,
  isOrganizationHydrationError,
  order,
}: {
  authorization?: Authorization
  organizationScope?: Pick<
    OrganizationScope,
    'scopeKind' | 'activeOrganizationId'
  >
  userID?: string
  isLoadingSelectedOrganization?: boolean
  isOrganizationHydrationError?: boolean
  order?: OrderPartyIdentity | null
}) => {
  if (
    !userID ||
    isLoadingSelectedOrganization ||
    isOrganizationHydrationError ||
    !canMutateDomain(authorization, 'orders', 'canUpdate') ||
    isGuestAuthorization(authorization)
  )
    return false
  // Manage requests can carry the requester's shop, not the recipient's shop.
  const shopOwnerID = order?.shop?.ownerID
  const isRequesterShop = isOrderRequesterShop(order)
  const shopOrganizationID = isRequesterShop
    ? undefined
    : order?.shop?.organizationID
  const recipientOrganizationID = order?.toOrganizationID
  if (
    shopOrganizationID &&
    recipientOrganizationID &&
    shopOrganizationID !== recipientOrganizationID
  )
    return false
  const sellerOrganizationID = shopOrganizationID || recipientOrganizationID
  if (sellerOrganizationID) {
    return (
      organizationScope?.scopeKind === 'organization' &&
      organizationScope.activeOrganizationId === sellerOrganizationID
    )
  }
  const recipientUserID = order?.toUser?.id
  const sellerShopOwnerID = isRequesterShop ? undefined : shopOwnerID
  if (
    sellerShopOwnerID &&
    recipientUserID &&
    sellerShopOwnerID !== recipientUserID
  )
    return false
  const sellerUserID = sellerShopOwnerID || recipientUserID
  return (
    organizationScope?.scopeKind === 'solo' &&
    Boolean(userID && sellerUserID === userID)
  )
}
